import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Settings, LogOut, Moon, Sun, Monitor, ChevronRight, User, Crown, Shield, HelpCircle } from 'lucide-react';
import { useTheme } from 'next-themes';
import { Button } from '@/components/ui/button';
import { MobileDrawer, MobileDrawerTrigger, MobileDrawerContent, MobileDrawerHeader, MobileDrawerTitle, MobileDrawerFooter } from '@/components/ui/mobile-drawer';
import { useState } from 'react';
import { cn } from '@/lib/utils';

export function ProfileDrawer() {
  const { signOut, profile } = useAuth();
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const [open, setOpen] = useState(false);

  const handleSignOut = async () => {
    await signOut();
    navigate('/auth');
    setOpen(false);
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    setOpen(false);
  };

  const themes = [
    { value: 'light', icon: Sun, label: 'Light' },
    { value: 'dark', icon: Moon, label: 'Dark' },
    { value: 'system', icon: Monitor, label: 'System' },
  ];

  const menuItems = [
    { icon: Settings, label: 'Settings', path: '/settings' },
    { icon: HelpCircle, label: 'Help & Support', path: '/support' },
  ];

  return (
    <MobileDrawer open={open} onOpenChange={setOpen}>
      <MobileDrawerTrigger asChild>
        <Button variant="ghost" size="icon" className="relative rounded-2xl">
          {profile?.avatar_url ? (
            <img 
              src={profile.avatar_url} 
              alt={profile?.name || 'User'} 
              className="w-9 h-9 rounded-2xl object-cover ring-2 ring-border"
            />
          ) : (
            <div className="w-9 h-9 rounded-2xl bg-gradient-primary flex items-center justify-center shadow-glow">
              <span className="text-white text-sm font-bold">
                {profile?.name?.[0]?.toUpperCase() || 'U'}
              </span>
            </div>
          )}
        </Button>
      </MobileDrawerTrigger>
      <MobileDrawerContent className="max-h-[85vh]">
        <MobileDrawerHeader className="pb-6">
          <div className="flex items-center gap-4">
            {profile?.avatar_url ? (
              <img 
                src={profile.avatar_url} 
                alt={profile?.name || 'User'} 
                className="w-16 h-16 rounded-3xl object-cover ring-2 ring-border shadow-card"
              />
            ) : (
              <div className="w-16 h-16 rounded-3xl bg-gradient-primary flex items-center justify-center shadow-glow">
                <span className="text-white text-2xl font-bold">
                  {profile?.name?.[0]?.toUpperCase() || 'U'}
                </span>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <MobileDrawerTitle className="text-lg truncate">
                {profile?.name || 'User'}
              </MobileDrawerTitle>
              <p className="text-sm text-muted-foreground truncate">{profile?.email}</p>
              {profile?.subscription && profile.subscription !== 'free' && (
                <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-semibold">
                  <Crown className="w-3 h-3" />
                  {profile.subscription.toUpperCase()}
                </span>
              )}
            </div>
          </div>
        </MobileDrawerHeader>

        {/* Theme Selector */}
        <div className="px-4 pb-4">
          <p className="text-xs font-medium text-muted-foreground mb-3 px-1">THEME</p>
          <div className="flex gap-2 p-1 bg-surface rounded-2xl">
            {themes.map((t) => {
              const Icon = t.icon;
              const isActive = theme === t.value;
              return (
                <button
                  key={t.value}
                  onClick={() => setTheme(t.value)}
                  className={cn(
                    "flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl transition-all duration-200",
                    isActive 
                      ? "bg-background shadow-card text-foreground" 
                      : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-sm font-medium">{t.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Menu Items */}
        <div className="px-4 pb-4 space-y-1">
          {menuItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.path}
                onClick={() => handleNavigate(item.path)}
                className="w-full flex items-center justify-between p-4 rounded-2xl hover:bg-surface transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-surface flex items-center justify-center">
                    <Icon className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <span className="font-medium">{item.label}</span>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </button>
            );
          })}
        </div>

        <MobileDrawerFooter className="pt-4 border-t border-border">
          <Button 
            variant="ghost" 
            onClick={handleSignOut}
            className="w-full h-12 rounded-2xl text-destructive hover:text-destructive hover:bg-destructive/10"
          >
            <LogOut className="w-5 h-5 mr-2" />
            Sign Out
          </Button>
        </MobileDrawerFooter>
      </MobileDrawerContent>
    </MobileDrawer>
  );
}
